import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useChatWebSocket } from "@/lib/useChatWebSocket";
import MusaCard from "@/components/MusaCard";
import PhotoUploader from "@/components/PhotoUploader";
import LiveVideoRecorder from "@/components/LiveVideoRecorder";
import PhotoManager from "@/components/PhotoManager";
import CategoryPicker from "@/components/CategoryPicker";
import { Crown, Eye, MessageCircle, Heart, Edit3, Plus, X, ShieldCheck, Shield, Star, ArrowRight, Check } from "lucide-react";

export default function Dashboard() {
  const { user, loaded } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (loaded && (!user || user === false)) nav("/login");
  }, [user, loaded, nav]);

  if (!user || user === false) return null;

  if (user.role === "agency") return <AgencyDashboard user={user} />;
  if (user.role === "companion" || user.role === "admin") return <CompanionDashboard user={user} />;
  return <VisitorDashboard user={user} />;
}

function VisitorDashboard({ user }) {
  const [favs, setFavs] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [tab, setTab] = useState("favs");

  useEffect(() => {
    api.get("/favorites").then((r) => setFavs(r.data || []));
    api.get("/messages/sent").then((r) => setInbox(r.data || []));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="visitor-dashboard">
      <div className="overline mb-3">Panel de visitante</div>
      <h1 className="font-serif text-5xl font-light mb-2" style={{ color: "var(--text-primary)" }}>Hola, {user.display_name}</h1>
      <p className="text-sm mb-10" style={{ color: "var(--text-secondary)" }}>{user.email}</p>

      <div className="flex gap-2 mb-8" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
        <TabBtn active={tab==="favs"} onClick={()=>setTab("favs")} testid="tab-favs"><Heart size={14} /> Favoritos ({favs.length})</TabBtn>
        <TabBtn active={tab==="sent"} onClick={()=>setTab("sent")} testid="tab-sent"><MessageCircle size={14} /> Mensajes enviados ({inbox.length})</TabBtn>
      </div>

      {tab === "favs" && (
        favs.length === 0 ? (
          <div className="text-center py-20" data-testid="no-favs">
            <p style={{ color: "var(--text-secondary)" }}>Aún no tienes favoritos.</p>
            <Link to="/galeria" className="btn-secondary mt-6 inline-flex">Explorar galería</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" data-testid="favs-grid">
            {favs.map((p, i) => <MusaCard key={p.id} profile={p} index={i} />)}
          </div>
        )
      )}

      {tab === "sent" && (
        <div className="flex flex-col gap-3" data-testid="sent-list">
          {inbox.length === 0 ? <p style={{ color: "var(--text-secondary)" }}>No has enviado mensajes.</p> :
            inbox.map((m) => (
              <div key={m.id} className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
                <div className="text-xs mb-2" style={{ color: "var(--brand-primary)" }}>{new Date(m.created_at).toLocaleString("es-MX")}</div>
                <p className="text-sm" style={{ color: "var(--text-primary)" }}>{m.body}</p>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

function CompanionDashboard({ user }) {
  const [profile, setProfile] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [searchParams] = useSearchParams();
  const isNewSignup = searchParams.get("new") === "1";
  const [tab, setTab] = useState(searchParams.get("tab") === "verify" ? "verify" : "profile");
  // If musa is not verified yet, force-land them on the verify tab
  // so they finish the documentation before doing anything else.
  React.useEffect(() => {
    if (profile && profile.verified === false && tab === "profile") {
      const seen = sessionStorage.getItem("pk2_seen_verify_redirect");
      if (!seen) {
        sessionStorage.setItem("pk2_seen_verify_redirect", "1");
        setTab("verify");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.verified]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [packages, setPackages] = useState([]);
  const [boostPkgs, setBoostPkgs] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState("");

  function emptyForm() {
    return {
      stage_name: "", bio: "", age: 21, city: "CDMX", state: "Ciudad de México",
      phone: "", whatsapp: "", services: "", languages: "", height_cm: "",
      hero_photo: "", photos: [], categories: [],
      rate_30min: "", rate_1h: "", rate_2h: "", rate_overnight: "", rate_outcall: "",
      rate_currency: "MXN", rate_notes: "",
    };
  }

  const load = async () => {
    try {
      const r = await api.get("/profiles/me");
      if (r.data) {
        setProfile(r.data);
        setForm({
          stage_name: r.data.stage_name || "",
          bio: r.data.bio || "",
          age: r.data.age || 21,
          city: r.data.city || "CDMX",
          state: r.data.state || "Ciudad de México",
          phone: r.data.phone || "",
          whatsapp: r.data.whatsapp || "",
          services: (r.data.services || []).join(", "),
          languages: (r.data.languages || []).join(", "),
          height_cm: r.data.height_cm || "",
          hero_photo: r.data.hero_photo || "",
          photos: r.data.photos || [],
          categories: r.data.categories || [],
          rate_30min: r.data.rate_30min || "",
          rate_1h: r.data.rate_1h || "",
          rate_2h: r.data.rate_2h || "",
          rate_overnight: r.data.rate_overnight || "",
          rate_outcall: r.data.rate_outcall || "",
          rate_currency: r.data.rate_currency || "MXN",
          rate_notes: r.data.rate_notes || "",
        });
      }
    } catch {/* ignore */}
    setLoaded(true);
  };

  useEffect(() => {
    load();
    api.get("/messages/inbox").then((r) => setMsgs(r.data || []));
    api.get("/cities").then((r) => setCities(r.data || []));
    api.get("/vip/packages").then((r) => setPackages(r.data || []));
    api.get("/boost/packages").then((r) => setBoostPkgs(r.data || []));
  }, []);

  // Realtime: append new messages as they arrive via WS
  useChatWebSocket(user?.id, (evt) => {
    if (evt && evt.type === "message" && evt.to_user_id === user.id) {
      setMsgs((m) => [{ ...evt }, ...m]);
    }
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    const payload = {
      stage_name: form.stage_name,
      bio: form.bio,
      age: parseInt(form.age),
      city: form.city,
      state: form.state,
      phone: form.phone,
      whatsapp: form.whatsapp,
      services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
      languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      categories: form.categories || [],
      height_cm: form.height_cm ? parseInt(form.height_cm) : null,
      hero_photo: form.hero_photo || null,
      photos: Array.isArray(form.photos) ? form.photos : form.photos.split("\n").map((s) => s.trim()).filter(Boolean),
      rate_30min: form.rate_30min ? parseInt(form.rate_30min) : null,
      rate_1h: form.rate_1h ? parseInt(form.rate_1h) : null,
      rate_2h: form.rate_2h ? parseInt(form.rate_2h) : null,
      rate_overnight: form.rate_overnight ? parseInt(form.rate_overnight) : null,
      rate_outcall: form.rate_outcall ? parseInt(form.rate_outcall) : null,
      rate_currency: form.rate_currency || "MXN",
      rate_notes: form.rate_notes || "",
    };
    try {
      const { data } = await api.post("/profiles", payload);
      setProfile(data);
      setEditing(false);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setSaving(false); }
  };

  const buyVip = async (pkgId) => {
    setCheckoutLoading(pkgId);
    try {
      const { data } = await api.post("/vip/checkout", { package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setCheckoutLoading("");
    }
  };

  const buyBoost = async (pkgId) => {
    setCheckoutLoading(pkgId);
    try {
      const { data } = await api.post("/boost/checkout", { package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setCheckoutLoading("");
    }
  };

  if (!loaded) return <div className="p-12" style={{ color: "var(--text-secondary)" }}>Cargando…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="companion-dashboard">
      <div className="overline mb-3">Panel de Musa</div>
      <h1 className="font-serif text-5xl font-light mb-2" style={{ color: "var(--text-primary)" }}>Hola, {user.display_name}</h1>
      <p className="text-sm mb-10" style={{ color: "var(--text-secondary)" }}>{user.email}</p>

      {isNewSignup && (
        <div
          className="p-6 mb-8"
          style={{
            background: "linear-gradient(135deg, rgba(212,175,55,0.20), rgba(255,0,51,0.10))",
            border: "2px solid var(--vip-gold)",
            boxShadow: "0 0 32px rgba(212,175,55,0.25)",
          }}
          data-testid="welcome-verify-banner"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div style={{ fontSize: 36 }}>👋</div>
            <div className="flex-1 min-w-[260px]">
              <h2 className="font-serif text-2xl mb-2" style={{ color: "var(--vip-gold)" }}>
                ¡Bienvenida a PK2 Scarlett!
              </h2>
              <p className="text-sm" style={{ color: "var(--text-primary)", lineHeight: 1.7 }}>
                Tu perfil ya está visible en la galería. Para recibir el
                <strong style={{ color: "var(--vip-gold)" }}> sello dorado de verificación</strong> y
                aparecer entre las destacadas, completa la verificación de identidad: INE (frente y reverso),
                selfie, selfie con INE en mano y un video grabado en vivo leyendo el guión de consentimiento.
              </p>
              <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                Tarda 5 minutos · Tus documentos solo los ve el equipo de PK2 Scarlett.
              </p>
            </div>
          </div>
        </div>
      )}

      {profile && !profile.verified && profile.status !== "banned" && !isNewSignup && (
        <div
          className="p-5 mb-8 flex flex-col md:flex-row items-start gap-3"
          style={{
            background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(200,16,46,0.08))",
            border: "1px solid var(--vip-gold)",
          }}
          data-testid="profile-verify-required-banner"
        >
          <ShieldCheck size={28} strokeWidth={1.2} color="var(--vip-gold)" />
          <div className="flex-1">
            <div className="font-serif text-lg" style={{ color: "var(--vip-gold)" }}>
              Verifica tu identidad para recibir el sello dorado
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>
              Sube INE (frente y reverso), selfie, selfie con INE en mano y un video grabado en vivo.
              Los perfiles verificados reciben <strong style={{ color: "var(--vip-gold)" }}>3× más visitas</strong> y mejor posición en la galería.
            </p>
          </div>
          <button onClick={() => setTab("verify")} className="btn-primary text-xs" style={{ background: "var(--vip-gold)", color: "#000", whiteSpace: "nowrap" }} data-testid="profile-verify-btn">
            Verificar ahora →
          </button>
        </div>
      )}

      {profile && profile.status === "banned" && (
        <div
          className="p-5 mb-8 flex items-start gap-3"
          style={{ background: "rgba(239,109,109,0.1)", border: "1px solid #ef6d6d" }}
          data-testid="profile-banned-banner"
        >
          <X size={28} strokeWidth={1.4} color="#ef6d6d" />
          <div>
            <div className="font-serif text-lg" style={{ color: "#ef6d6d" }}>Tu perfil fue rechazado</div>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>
              Tu registro no cumplió con los lineamientos. Corrige los datos, sube fotos de mejor calidad y escribe al soporte si crees que fue un error.
            </p>
          </div>
        </div>
      )}

      {profile && profile.is_vip && (() => {
        const until = profile.vip_until ? new Date(profile.vip_until) : null;
        if (!until) return null;
        const diffMs = until.getTime() - Date.now();
        const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        if (diffMs <= 0) return null;
        const isExpiringSoon = daysLeft <= 3;
        return (
          <div
            className="p-4 mb-8 flex items-center justify-between gap-3 flex-wrap"
            style={{
              background: isExpiringSoon
                ? "linear-gradient(135deg, rgba(239,109,109,0.18), rgba(212,175,55,0.10))"
                : "linear-gradient(135deg, rgba(212,175,55,0.14), rgba(200,16,46,0.06))",
              border: `1px solid ${isExpiringSoon ? "#ef6d6d" : "var(--vip-gold)"}`,
            }}
            data-testid="vip-countdown-banner"
          >
            <div className="flex items-center gap-3">
              <Crown size={22} color={isExpiringSoon ? "#ef6d6d" : "var(--vip-gold)"} />
              <div>
                <div className="font-serif text-base" style={{ color: "var(--text-primary)" }}>
                  {isExpiringSoon
                    ? `⚠️ Tu VIP expira en ${daysLeft} día${daysLeft === 1 ? "" : "s"}`
                    : `Tu VIP está activo · ${daysLeft} días restantes`}
                </div>
                <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  Vence el {until.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setTab("vip")}
              className="btn-primary text-xs"
              style={isExpiringSoon ? {} : { background: "var(--vip-gold)", color: "#000" }}
              data-testid="vip-renew-btn"
            >
              {isExpiringSoon ? "Renovar ahora →" : "Extender VIP →"}
            </button>
          </div>
        );
      })()}

      {profile && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat label="Vistas" value={profile.views} icon={<Eye size={16} />} />
          <Stat label="Estado" value={profile.status} icon={<Edit3 size={16} />} />
          <Stat label="VIP" value={profile.is_vip ? "Activo" : "Inactivo"} icon={<Crown size={16} />} gold={profile.is_vip} />
          <Stat label="Mensajes" value={msgs.length} icon={<MessageCircle size={16} />} />
        </div>
      )}

      <div className="flex gap-2 mb-8 overflow-x-auto" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
        <TabBtn active={tab==="profile"} onClick={()=>setTab("profile")} testid="tab-profile">Mi perfil</TabBtn>
        <TabBtn active={tab==="photos"} onClick={()=>setTab("photos")} testid="tab-photos">Fotos</TabBtn>
        <TabBtn active={tab==="analytics"} onClick={()=>setTab("analytics")} testid="tab-analytics">Analytics</TabBtn>
        <TabBtn active={tab==="verify"} onClick={()=>setTab("verify")} testid="tab-verify"><ShieldCheck size={14} /> Verificación</TabBtn>
        <TabBtn active={tab==="vip"} onClick={()=>setTab("vip")} testid="tab-vip"><Crown size={14} /> VIP</TabBtn>
        <TabBtn active={tab==="messages"} onClick={()=>setTab("messages")} testid="tab-messages"><MessageCircle size={14} /> Mensajes ({msgs.length})</TabBtn>
      </div>

      {tab === "profile" && (
        <>
          {(!profile || editing) ? (
            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="profile-form">
              <input required className="input-field md:col-span-2" placeholder="Nombre artístico" value={form.stage_name} onChange={(e)=>setForm({...form, stage_name:e.target.value})} data-testid="form-stage-name" />
              <input required type="number" min="18" max="70" className="input-field" placeholder="Edad" value={form.age} onChange={(e)=>setForm({...form, age:e.target.value})} data-testid="form-age" />
              <input type="number" className="input-field" placeholder="Estatura (cm)" value={form.height_cm} onChange={(e)=>setForm({...form, height_cm:e.target.value})} data-testid="form-height" />
              <select className="input-field" value={form.city} onChange={(e)=>{
                const c = cities.find((x)=>x.city===e.target.value);
                setForm({...form, city:e.target.value, state: c?.state || form.state});
              }} data-testid="form-city">
                {cities.map((c) => <option key={c.slug} value={c.city}>{c.city}</option>)}
              </select>
              <input className="input-field" placeholder="Estado" value={form.state} onChange={(e)=>setForm({...form, state:e.target.value})} data-testid="form-state" />
              <input className="input-field" placeholder="WhatsApp (+52 55 1234 5678)" value={form.whatsapp} onChange={(e)=>setForm({...form, whatsapp:e.target.value})} data-testid="form-whatsapp" />
              <input className="input-field" placeholder="Teléfono" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} data-testid="form-phone" />
              <textarea className="input-field md:col-span-2 h-32 resize-none" placeholder="Biografía (máx. 2000)" value={form.bio} onChange={(e)=>setForm({...form, bio:e.target.value})} data-testid="form-bio" />
              <input className="input-field md:col-span-2" placeholder="Servicios (separa por comas)" value={form.services} onChange={(e)=>setForm({...form, services:e.target.value})} data-testid="form-services" />
              <input className="input-field md:col-span-2" placeholder="Idiomas (separa por comas)" value={form.languages} onChange={(e)=>setForm({...form, languages:e.target.value})} data-testid="form-languages" />

              {/* Tariffs */}
              <div className="md:col-span-2 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }} data-testid="form-tariffs">
                <div className="overline mb-3" style={{ color: "var(--vip-gold)" }}>Tarifas (opcional)</div>
                <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
                  Publica tus tarifas para que los clientes ya las conozcan antes de contactarte. Deja en blanco lo que no aplique.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: "rate_30min", label: "30 min" },
                    { key: "rate_1h", label: "1 hora" },
                    { key: "rate_2h", label: "2 horas" },
                    { key: "rate_overnight", label: "Toda la noche" },
                    { key: "rate_outcall", label: "Salida / hotel" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="text-xs block mb-1" style={{ color: "var(--text-secondary)" }}>{f.label}</label>
                      <input
                        type="number" min="0" step="50"
                        className="input-field w-full"
                        placeholder="$ MXN"
                        value={form[f.key]}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        data-testid={`form-${f.key}`}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="text-xs block mb-1" style={{ color: "var(--text-secondary)" }}>Moneda</label>
                    <select
                      className="input-field w-full"
                      value={form.rate_currency}
                      onChange={(e) => setForm({ ...form, rate_currency: e.target.value })}
                      data-testid="form-rate-currency"
                    >
                      <option value="MXN">MXN</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
                <input
                  className="input-field mt-3 w-full"
                  placeholder="Notas sobre tarifas (ej: anticipo 50%, depósito requerido)"
                  maxLength={300}
                  value={form.rate_notes}
                  onChange={(e) => setForm({ ...form, rate_notes: e.target.value })}
                  data-testid="form-rate-notes"
                />
              </div>

              <div className="md:col-span-2 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                <CategoryPicker
                  value={form.categories || []}
                  onChange={(cats) => setForm({ ...form, categories: cats })}
                  testid="form-categories"
                />
              </div>
              <div className="md:col-span-2 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                <PhotoManager
                  photos={form.photos || []}
                  heroPhoto={form.hero_photo || ""}
                  onChange={(next) => setForm({ ...form, photos: next })}
                  onHeroChange={(url) => setForm({ ...form, hero_photo: url })}
                  testid="musa-photo-manager"
                />
              </div>
              {error && <p className="md:col-span-2 text-xs" style={{ color: "#ef6d6d" }} data-testid="profile-form-error">{error}</p>}
              <div className="md:col-span-2 flex gap-3">
                <button className="btn-primary flex-1" disabled={saving} data-testid="form-save">{saving?"Guardando…":"Guardar perfil"}</button>
                {profile && <button type="button" className="btn-ghost" onClick={()=>setEditing(false)}>Cancelar</button>}
              </div>
            </form>
          ) : (
            <div className="p-8" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="font-serif text-3xl font-light" style={{ color: "var(--text-primary)" }}>{profile.stage_name}</h2>
                  <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>{profile.age} años · {profile.city}, {profile.state}</p>
                </div>
                <button className="btn-secondary" onClick={()=>setEditing(true)} data-testid="edit-profile-btn"><Edit3 size={14} /> Editar</button>
              </div>
              {profile.hero_photo && <img src={profile.hero_photo} alt={profile.stage_name} className="w-full max-w-md h-auto mb-5" style={{ aspectRatio: "3/4", objectFit: "cover" }} />}
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{profile.bio}</p>
              <div className="mt-5 pt-5" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                <Link to={`/musa/${profile.id}`} className="btn-ghost text-xs" data-testid="view-public-profile">Ver perfil público →</Link>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "photos" && profile && (
        <PhotosTab profile={profile} onRefresh={load} />
      )}

      {tab === "analytics" && profile && (
        <AnalyticsTab profileId={profile.id} />
      )}

      {tab === "verify" && profile && (
        <VerificationTab profile={profile} />
      )}

      {tab === "vip" && (
        <div data-testid="vip-tab">
          {boostPkgs.length > 0 && (
            <div className="mb-10" data-testid="boost-section">
              <div className="overline mb-3" style={{ color: "var(--brand-primary)" }}>Mini-promoción · Económico</div>
              <p className="text-sm mb-5 max-w-xl" style={{ color: "var(--text-secondary)" }}>
                ¿No quieres comprometer una semana? <strong style={{ color: "var(--brand-primary)" }}>Boost 24h</strong> te pone al inicio de la galería durante 24 horas por sólo $99 MXN.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {boostPkgs.map((p) => (
                  <div key={p.id} className="p-6 flex items-center justify-between" data-testid={`boost-card-${p.id}`}
                    style={{ background: "linear-gradient(135deg, rgba(200,16,46,0.18), rgba(127,29,29,0.10))", border: "1px solid var(--brand-primary)", boxShadow: "var(--shadow-red)" }}>
                    <div>
                      <div className="overline mb-1">Boost · {p.hours}h</div>
                      <h3 className="font-serif text-2xl" style={{ color: "var(--text-primary)" }}>{p.label}</h3>
                      <div className="font-serif text-3xl font-light mt-1" style={{ color: "var(--brand-primary)" }}>
                        ${p.amount.toLocaleString("es-MX")} <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{p.currency.toUpperCase()}</span>
                      </div>
                    </div>
                    <button className="btn-primary" disabled={!profile || checkoutLoading===p.id} onClick={()=>buyBoost(p.id)} data-testid={`boost-buy-${p.id}`}>
                      {checkoutLoading===p.id ? "…" : "Activar"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="divider-ornament mb-6"><span className="overline">Plan semanal o mensual</span></div>

          <p className="mb-6 text-sm max-w-xl" style={{ color: "var(--text-secondary)" }}>
            Aumenta tu visibilidad y obtén el sello VIP dorado. Tu perfil aparecerá primero en la galería y será destacado en la página principal.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packages.map((p) => (
              <div key={p.id} className="p-6" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }} data-testid={`vip-card-${p.id}`}>
                <div className="flex items-center gap-2 vip-badge mb-4"><Crown size={10} /> VIP</div>
                <h3 className="font-serif text-2xl mb-1" style={{ color: "var(--text-primary)" }}>{p.label}</h3>
                <div className="font-serif text-4xl font-light" style={{ color: "var(--vip-gold)" }}>
                  ${p.amount.toLocaleString("es-MX")} <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{p.currency.toUpperCase()}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Duración: {p.days} días</p>
                <button className="btn-primary w-full mt-6" disabled={!profile || checkoutLoading===p.id} onClick={()=>buyVip(p.id)} data-testid={`vip-buy-${p.id}`}>
                  {checkoutLoading===p.id ? "Redirigiendo…" : "Activar VIP"}
                </button>
              </div>
            ))}
          </div>

          {error && <p className="text-xs mt-4" style={{ color: "#ef6d6d" }} data-testid="vip-error">{error}</p>}
          {!profile && <p className="mt-5 text-xs" style={{ color: "var(--text-muted)" }}>Primero crea tu perfil para activar VIP.</p>}
        </div>
      )}

      {tab === "messages" && (
        <div className="flex flex-col gap-3" data-testid="messages-list">
          {msgs.length === 0 ? <p style={{ color: "var(--text-secondary)" }}>Sin mensajes todavía.</p> :
            msgs.map((m) => (
              <div key={m.id} className="p-5" style={{ background: "var(--bg-surface)", border: `1px solid ${m.read ? "var(--border-subtle)" : "var(--border-accent)"}` }}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm" style={{ color: "var(--brand-primary)" }}>{m.from_display_name}</span>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>{new Date(m.created_at).toLocaleString("es-MX")}</span>
                </div>
                <p className="text-sm" style={{ color: "var(--text-primary)" }}>{m.body}</p>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, gold }) {
  return (
    <div className="p-4" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
      <div className="flex items-center gap-2 overline mb-2" style={{ color: gold ? "var(--vip-gold)" : undefined }}>
        {icon} {label}
      </div>
      <div className="font-serif text-2xl" style={{ color: gold ? "var(--vip-gold)" : "var(--text-primary)" }}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors whitespace-nowrap"
      style={{
        color: active ? "var(--brand-primary)" : "var(--text-secondary)",
        borderBottom: `2px solid ${active ? "var(--brand-primary)" : "transparent"}`,
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function PhotosTab({ profile, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState([]);

  const loadPending = async () => {
    try {
      const { data } = await api.get("/photos/mine");
      setPending((data || []).filter((p) => p.status === "pending"));
    } catch {/* ignore */}
  };

  useEffect(() => { loadPending(); }, []);

  const saveList = async (newPhotos, newHero) => {
    setSaving(true); setError("");
    try {
      const payload = {
        stage_name: profile.stage_name, bio: profile.bio, age: profile.age,
        city: profile.city, state: profile.state, phone: profile.phone || "",
        whatsapp: profile.whatsapp || "", services: profile.services || [],
        languages: profile.languages || [], height_cm: profile.height_cm,
        photos: newPhotos !== undefined ? newPhotos : (profile.photos || []),
        hero_photo: newHero !== undefined ? newHero : profile.hero_photo,
      };
      await api.post("/profiles", payload);
      onRefresh && onRefresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const addPhotos = async (urls) => {
    const arr = Array.isArray(urls) ? urls : [urls];
    setSaving(true); setError("");
    try {
      for (const u of arr) {
        await api.post("/photos/submit", { url: u, folder: "profiles/" });
      }
      await loadPending();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const removePhoto = (url) => {
    const newList = (profile.photos || []).filter((u) => u !== url);
    const newHero = url === profile.hero_photo ? (newList[0] || null) : profile.hero_photo;
    saveList(newList, newHero);
  };

  const makeHero = (url) => {
    const list = [profile.hero_photo, ...(profile.photos || []).filter((u) => u !== url)].filter(Boolean);
    saveList(list, url);
  };

  const gallery = [profile.hero_photo, ...(profile.photos || [])].filter(Boolean);

  return (
    <div data-testid="photos-tab">
      <p className="mb-6 text-sm max-w-xl" style={{ color: "var(--text-secondary)" }}>
        Sube tus mejores fotos. <strong style={{ color: "var(--brand-primary)" }}>Nuestro equipo revisa cada foto antes de publicarla</strong> (normalmente en menos de 1 hora). Máximo 10 MB por archivo, JPG/PNG/WEBP.
      </p>
      <PhotoUploader folder="profiles/" multiple onUploaded={addPhotos} testid="profile-photos-uploader" />
      {error && <p className="text-xs mt-3" style={{ color: "#ef6d6d" }} data-testid="photos-error">{error}</p>}
      {saving && <p className="text-xs mt-3" style={{ color: "var(--brand-primary)" }}>Enviando a moderación…</p>}

      {pending.length > 0 && (
        <>
          <div className="overline mt-10 mb-3">Pendientes de aprobación ({pending.length})</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="pending-photos-grid">
            {pending.map((p) => (
              <div key={p.id} className="relative" style={{ aspectRatio: "3/4", background: "var(--bg-surface)", border: "1px dashed var(--brand-primary)" }}>
                <img src={p.url} alt="" className="w-full h-full object-cover opacity-60" />
                <span className="absolute top-2 left-2 text-xs px-2 py-1" style={{ background: "rgba(200,16,46,0.85)", color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase" }}>En revisión</span>
              </div>
            ))}
          </div>
        </>
      )}

      {gallery.length > 0 && (
        <>
          <div className="overline mt-10 mb-3">Tus fotos actuales</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photos-grid">
            {gallery.map((u) => (
              <div key={u} className="relative group" style={{ aspectRatio: "3/4", background: "var(--bg-surface)" }}>
                <img src={u} alt="" className="w-full h-full object-cover" />
                {u === profile.hero_photo && (
                  <span className="absolute top-2 left-2 vip-badge" style={{ padding: "0.2rem 0.5rem" }}>PORTADA</span>
                )}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2"
                     style={{ background: "rgba(8,7,8,0.75)" }}>
                  {u !== profile.hero_photo && (
                    <button className="btn-secondary text-xs" style={{ padding: "0.4rem 0.7rem" }} onClick={() => makeHero(u)} data-testid="make-hero-btn">
                      Portada
                    </button>
                  )}
                  <button className="btn-ghost text-xs" style={{ color: "#ef6d6d" }} onClick={() => removePhoto(u)} data-testid="remove-photo-btn">
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function VerificationTab({ profile }) {
  const [verif, setVerif] = useState(null);
  const [step, setStep] = useState(0); // 0..4 (5 steps) + 5 = review/submit
  const [idFrontUrl, setIdFrontUrl] = useState("");
  const [idBackUrl, setIdBackUrl] = useState("");
  const [selfieUrl, setSelfieUrl] = useState("");
  const [selfieWithIdUrl, setSelfieWithIdUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [voluntaryConsent, setVoluntaryConsent] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/verification/mine").then((r) => setVerif(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.post("/verification", {
        id_front_url: idFrontUrl,
        id_back_url: idBackUrl,
        selfie_url: selfieUrl,
        selfie_with_id_url: selfieWithIdUrl,
        video_url: videoUrl,
        voluntary_consent: voluntaryConsent,
        notes,
      });
      setVerif(data);
      setIdFrontUrl(""); setIdBackUrl(""); setSelfieUrl(""); setSelfieWithIdUrl("");
      setVideoUrl(""); setNotes(""); setVoluntaryConsent(false); setStep(0);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (profile.verified) {
    return (
      <div className="p-8 flex items-center gap-4" style={{ background: "var(--bg-surface)", border: "1px solid var(--vip-gold)" }} data-testid="verified-badge-status">
        <ShieldCheck size={32} strokeWidth={1.2} color="var(--vip-gold)" />
        <div>
          <div className="font-serif text-2xl" style={{ color: "var(--vip-gold)" }}>Perfil verificado</div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Tu identidad fue validada por el equipo PK2 Scarlett.</p>
        </div>
      </div>
    );
  }

  const STEPS = [
    {
      key: "id_front",
      title: "INE · Frente",
      hint: "Foto clara de la cara frontal de tu identificación oficial. Se debe leer tu nombre y foto.",
      value: idFrontUrl,
      setValue: setIdFrontUrl,
      testid: "verif-id-front",
    },
    {
      key: "id_back",
      title: "INE · Reverso",
      hint: "Foto de la cara trasera de la misma identificación. Sin recortes ni reflejos.",
      value: idBackUrl,
      setValue: setIdBackUrl,
      testid: "verif-id-back",
    },
    {
      key: "selfie",
      title: "Selfie tuya",
      hint: "Una selfie normal mirando a la cámara, sin lentes oscuros ni gorra. Buena iluminación.",
      value: selfieUrl,
      setValue: setSelfieUrl,
      testid: "verif-selfie",
    },
    {
      key: "selfie_with_id",
      title: "Selfie con INE en mano",
      hint: "Selfie sosteniendo tu INE al lado de tu cara. Los datos del INE deben ser legibles.",
      value: selfieWithIdUrl,
      setValue: setSelfieWithIdUrl,
      testid: "verif-selfie-with-id",
    },
    {
      key: "video",
      title: "Video en vivo",
      hint: "Se graba DIRECTO desde tu cámara. No se aceptan videos subidos.",
      value: videoUrl,
      setValue: setVideoUrl,
      testid: "verif-video",
    },
  ];

  const total = STEPS.length;
  const current = STEPS[step];
  const done = step >= total;
  const allReady = idFrontUrl && idBackUrl && selfieUrl && selfieWithIdUrl && videoUrl;

  return (
    <div data-testid="verification-tab">
      <div className="mb-6">
        <h3 className="font-serif text-2xl mb-2" style={{ color: "var(--text-primary)" }}>Verificación de identidad</h3>
        <p className="text-sm max-w-xl" style={{ color: "var(--text-secondary)" }}>
          Completa los 5 pasos en orden para obtener el sello dorado de verificación.
          Revisión en <strong style={{ color: "var(--brand-primary)" }}>24-48 h</strong>.
        </p>
      </div>

      {verif && verif.status === "pending" && (
        <div className="p-5 mb-6" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }} data-testid="verif-pending">
          <div className="overline mb-1" style={{ color: "var(--brand-primary)" }}>En revisión</div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Tu solicitud está siendo revisada por nuestro equipo.</p>
        </div>
      )}

      {verif && verif.status === "rejected" && (
        <div className="p-5 mb-6" style={{ background: "var(--bg-surface)", border: "1px solid #ef6d6d" }} data-testid="verif-rejected">
          <div className="overline mb-1" style={{ color: "#ef6d6d" }}>Rechazada</div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Vuelve a enviar las 5 pruebas con mejor calidad e iluminación.</p>
        </div>
      )}

      {/* Stepper indicator */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="verif-stepper">
        {STEPS.map((s, i) => {
          const complete = !!s.value;
          const isCurrent = i === step && !done;
          return (
            <React.Fragment key={s.key}>
              <button
                type="button"
                onClick={() => setStep(i)}
                className="flex items-center gap-2 px-3 py-2 text-xs transition-all"
                style={{
                  background: isCurrent ? "var(--brand-primary)" : complete ? "var(--bg-elevated)" : "var(--bg-surface)",
                  color: isCurrent ? "#fff" : complete ? "var(--vip-gold)" : "var(--text-secondary)",
                  border: `1px solid ${isCurrent ? "var(--brand-primary)" : complete ? "var(--vip-gold)" : "var(--border-subtle)"}`,
                }}
                data-testid={`verif-step-${i}`}
              >
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 20, height: 20, borderRadius: "50%",
                    background: complete ? "var(--vip-gold)" : isCurrent ? "#fff" : "transparent",
                    color: complete ? "#000" : isCurrent ? "var(--brand-primary)" : "inherit",
                    border: !complete && !isCurrent ? "1px solid var(--border-subtle)" : "none",
                    fontWeight: 700,
                    fontSize: 11,
                  }}
                >
                  {complete ? <Check size={12} /> : i + 1}
                </span>
                <span className="hidden sm:inline">{s.title}</span>
              </button>
              {i < STEPS.length - 1 && (
                <span style={{ color: "var(--text-muted)" }}>·</span>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Current step */}
      {!done && (
        <div className="p-6" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }} data-testid={`${current.testid}-panel`}>
          <div className="overline mb-2" style={{ color: "var(--brand-primary)" }}>
            Paso {step + 1} de {total}
          </div>
          <h4 className="font-serif text-2xl mb-2" style={{ color: "var(--text-primary)" }}>
            {current.title}
          </h4>
          <p className="text-xs mb-5" style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>
            {current.hint}
          </p>

          {current.key === "video" ? (
            current.value ? (
              <div className="relative" style={{ aspectRatio: "3/4", background: "var(--bg-elevated)" }}>
                <video src={current.value} controls playsInline className="w-full h-full object-cover" />
                <button
                  className="btn-ghost text-xs absolute top-2 right-2"
                  onClick={() => current.setValue("")}
                  data-testid={`${current.testid}-remove`}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <LiveVideoRecorder onUploaded={(url) => current.setValue(url)} testid={current.testid} />
            )
          ) : (
            current.value ? (
              <div className="relative" style={{ aspectRatio: "3/2", background: "var(--bg-elevated)" }}>
                <img src={current.value} alt="" className="w-full h-full object-cover" />
                <button
                  className="btn-ghost text-xs absolute top-2 right-2"
                  onClick={() => current.setValue("")}
                  data-testid={`${current.testid}-remove`}
                >
                  <X size={14} /> Cambiar foto
                </button>
              </div>
            ) : (
              <PhotoUploader
                folder="verifications/"
                multiple={false}
                onUploaded={(u) => current.setValue(u)}
                testid={`${current.testid}-uploader`}
              />
            )
          )}

          <div className="flex justify-between mt-6">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              data-testid="verif-prev-btn"
            >
              ← Anterior
            </button>
            <button
              type="button"
              className="btn-primary text-xs"
              onClick={() => setStep(step + 1)}
              disabled={!current.value}
              data-testid="verif-next-btn"
            >
              {step === total - 1 ? "Revisar envío" : "Siguiente"} <ArrowRight size={14} className="inline ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Review & submit */}
      {done && (
        <div className="p-6" style={{ background: "var(--bg-surface)", border: "1px solid var(--vip-gold)" }} data-testid="verif-review">
          <div className="overline mb-3" style={{ color: "var(--vip-gold)" }}>Revisión final</div>
          <h4 className="font-serif text-2xl mb-4" style={{ color: "var(--text-primary)" }}>
            Listo para enviar
          </h4>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {STEPS.map((s) => (
              <div key={s.key} className="relative" style={{ aspectRatio: "3/4", background: "var(--bg-elevated)" }}>
                {s.key === "video" ? (
                  <video src={s.value} muted className="w-full h-full object-cover" />
                ) : (
                  <img src={s.value} alt={s.title} className="w-full h-full object-cover" />
                )}
                <span
                  className="absolute bottom-0 left-0 right-0 text-[10px] uppercase tracking-widest px-2 py-1 text-center"
                  style={{ background: "rgba(10,10,10,0.85)", color: "var(--text-primary)" }}
                >
                  {s.title}
                </span>
              </div>
            ))}
          </div>

          <textarea
            className="input-field mb-4 h-20 resize-none text-sm"
            placeholder="Notas adicionales (opcional) — por ejemplo: horario preferido, canal alterno."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="verif-notes"
          />

          {/* Voluntary consent — required to submit */}
          <label
            className="flex items-start gap-3 p-4 mb-4 cursor-pointer"
            style={{
              background: voluntaryConsent ? "rgba(212,175,55,0.10)" : "var(--bg-elevated)",
              border: `1px solid ${voluntaryConsent ? "var(--vip-gold)" : "var(--border-subtle)"}`,
              transition: "border-color 0.2s",
            }}
            data-testid="verif-consent-wrap"
          >
            <input
              type="checkbox"
              className="mt-1"
              checked={voluntaryConsent}
              onChange={(e) => setVoluntaryConsent(e.target.checked)}
              style={{ accentColor: "var(--vip-gold)", width: 18, height: 18, flexShrink: 0 }}
              data-testid="verif-consent-checkbox"
            />
            <span className="text-xs" style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
              <strong style={{ color: "var(--vip-gold)" }}>Consentimiento informado:</strong> declaro
              bajo protesta de decir verdad que <strong>soy mayor de 18 años</strong>, que las fotos
              y video son míos, que <strong>publico mi perfil por mi propia voluntad</strong>, que
              <strong> nadie me está obligando ni recibe pago por mí</strong>, y que entiendo
              que esta plataforma es solo un directorio publicitario para adultos.
            </span>
          </label>

          {error && <p className="text-xs mb-3" style={{ color: "#ef6d6d" }} data-testid="verif-error">{error}</p>}

          <div className="flex gap-3">
            <button
              type="button"
              className="btn-ghost text-xs flex-1"
              onClick={() => setStep(total - 1)}
              data-testid="verif-back-to-steps"
            >
              ← Volver a editar
            </button>
            <button
              className="btn-primary flex-1"
              disabled={!allReady || !voluntaryConsent || saving}
              onClick={submit}
              data-testid="verif-submit"
            >
              {saving ? "Enviando…" : "Enviar verificación"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AgencyDashboard({ user }) {
  const [profiles, setProfiles] = useState([]);
  const [cities, setCities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProfile());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  function emptyProfile() {
    return { stage_name: "", bio: "", age: 21, city: "CDMX", state: "Ciudad de México",
             whatsapp: "", phone: "", services: "", languages: "", height_cm: "",
             hero_photo: "", photos: [], categories: [] };
  }

  const load = async () => {
    const r = await api.get("/agency/profiles");
    setProfiles(r.data || []);
  };
  useEffect(() => { load(); api.get("/cities").then((r) => setCities(r.data || [])); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    const payload = {
      stage_name: form.stage_name, bio: form.bio, age: parseInt(form.age),
      city: form.city, state: form.state, phone: form.phone, whatsapp: form.whatsapp,
      services: (form.services || "").split(",").map((s) => s.trim()).filter(Boolean),
      languages: (form.languages || "").split(",").map((s) => s.trim()).filter(Boolean),
      categories: form.categories || [],
      height_cm: form.height_cm ? parseInt(form.height_cm) : null,
      hero_photo: form.hero_photo || null,
      photos: form.photos || [],
    };
    try {
      if (editing) await api.put(`/agency/profiles/${editing.id}`, payload);
      else await api.post("/agency/profiles", payload);
      setShowForm(false); setEditing(null); setForm(emptyProfile());
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      stage_name: p.stage_name, bio: p.bio, age: p.age, city: p.city, state: p.state,
      whatsapp: p.whatsapp || "", phone: p.phone || "",
      services: (p.services || []).join(", "), languages: (p.languages || []).join(", "),
      height_cm: p.height_cm || "", hero_photo: p.hero_photo || "",
      photos: p.photos || [],
      categories: p.categories || [],
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este perfil?")) return;
    await api.delete(`/agency/profiles/${id}`);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="agency-dashboard">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="overline mb-3">Panel de Agencia</div>
          <h1 className="font-serif text-5xl font-light" style={{ color: "var(--text-primary)" }}>Hola, {user.display_name}</h1>
          <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>{profiles.length} perfil{profiles.length === 1 ? "" : "es"} bajo tu gestión</p>
        </div>
        <button className="btn-primary" onClick={() => { setEditing(null); setForm(emptyProfile()); setShowForm(true); }} data-testid="agency-new-profile">
          <Plus size={14} /> Nueva musa
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 mb-8" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }} data-testid="agency-form">
          <input required className="input-field md:col-span-2" placeholder="Nombre artístico" value={form.stage_name} onChange={(e)=>setForm({...form, stage_name:e.target.value})} />
          <input required type="number" min="18" max="70" className="input-field" placeholder="Edad" value={form.age} onChange={(e)=>setForm({...form, age:e.target.value})} />
          <input type="number" className="input-field" placeholder="Estatura (cm)" value={form.height_cm} onChange={(e)=>setForm({...form, height_cm:e.target.value})} />
          <select className="input-field" value={form.city} onChange={(e)=>{
            const c = cities.find((x)=>x.city===e.target.value);
            setForm({...form, city:e.target.value, state: c?.state || form.state});
          }}>
            {cities.map((c) => <option key={c.slug} value={c.city}>{c.city}</option>)}
          </select>
          <input className="input-field" placeholder="Estado" value={form.state} onChange={(e)=>setForm({...form, state:e.target.value})} />
          <input className="input-field md:col-span-2" placeholder="WhatsApp (+52 55 …)" value={form.whatsapp} onChange={(e)=>setForm({...form, whatsapp:e.target.value})} />
          <textarea className="input-field md:col-span-2 h-24 resize-none" placeholder="Biografía" value={form.bio} onChange={(e)=>setForm({...form, bio:e.target.value})} />
          <input className="input-field md:col-span-2" placeholder="Servicios (separa por comas)" value={form.services} onChange={(e)=>setForm({...form, services:e.target.value})} />
          <input className="input-field md:col-span-2" placeholder="Idiomas (separa por comas)" value={form.languages} onChange={(e)=>setForm({...form, languages:e.target.value})} />
          <div className="md:col-span-2 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <CategoryPicker
              value={form.categories || []}
              onChange={(cats) => setForm({ ...form, categories: cats })}
              testid="agency-form-categories"
            />
          </div>
          <div className="md:col-span-2">
            <PhotoManager
              photos={form.photos || []}
              heroPhoto={form.hero_photo || ""}
              onChange={(next) => setForm({ ...form, photos: next })}
              onHeroChange={(url) => setForm({ ...form, hero_photo: url })}
              testid="agency-photo-manager"
            />
          </div>
          {error && <p className="md:col-span-2 text-xs" style={{ color: "#ef6d6d" }}>{error}</p>}
          <div className="md:col-span-2 flex gap-3">
            <button className="btn-primary flex-1" disabled={saving}>{saving?"Guardando…":editing?"Actualizar":"Crear"}</button>
            <button type="button" className="btn-ghost" onClick={()=>{setShowForm(false); setEditing(null);}}>Cancelar</button>
          </div>
        </form>
      )}

      {profiles.length === 0 ? (
        <p className="text-center py-12" style={{ color: "var(--text-secondary)" }}>
          Aún no tienes perfiles. Crea el primero con el botón de arriba.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {profiles.map((p) => (
            <div key={p.id} className="relative group" data-testid={`agency-profile-${p.id}`}>
              <MusaCard profile={p} />
              <div className="flex gap-2 mt-2">
                <button className="btn-ghost text-xs flex-1" onClick={() => startEdit(p)} data-testid={`edit-${p.id}`}><Edit3 size={12} /> Editar</button>
                <button className="btn-ghost text-xs" style={{ color: "#ef6d6d" }} onClick={() => remove(p.id)} data-testid={`delete-${p.id}`}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Analytics tab — per-profile KPIs for the logged-in Musa
// ────────────────────────────────────────────────────────────
function AnalyticsTab({ profileId }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!profileId) return;
    api.get(`/analytics/profile/${profileId}`)
      .then((r) => setStats(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [profileId]);

  if (error) return <p className="text-sm" style={{ color: "#ef6d6d" }} data-testid="analytics-error">{error}</p>;
  if (!stats) return <p className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="analytics-loading">Cargando estadísticas…</p>;

  const timeline = stats.timeline || [];
  const max = Math.max(1, ...timeline.map((d) => d.count || 0));

  return (
    <div data-testid="analytics-tab">
      <p className="mb-6 text-sm max-w-xl" style={{ color: "var(--text-secondary)" }}>
        Últimos {stats.days || 30} días. Aquí puedes medir la efectividad de tu perfil.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="analytics-kpis">
        {[
          { label: "Vistas", value: stats.views || 0 },
          { label: "Clics WhatsApp", value: stats.whatsapp_clicks || 0 },
          { label: "Mensajes", value: stats.messages || 0 },
          { label: "Favoritos", value: stats.favorites || 0 },
        ].map((k) => (
          <div key={k.label} className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }} data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="overline mb-1">{k.label}</div>
            <div className="font-serif text-3xl font-light" style={{ color: "var(--brand-primary)" }}>
              {k.value.toLocaleString("es-MX")}
            </div>
          </div>
        ))}
      </div>

      {stats.conversion_rate !== undefined && (
        <div className="p-5 mb-6" style={{ background: "var(--bg-surface)", border: "1px solid var(--vip-gold)" }}>
          <div className="overline mb-1" style={{ color: "var(--vip-gold)" }}>Tasa de conversión</div>
          <div className="font-serif text-2xl font-light" style={{ color: "var(--vip-gold)" }}>
            {(stats.conversion_rate * 100).toFixed(1)}%
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            Porcentaje de visitantes que te contactan.
          </p>
        </div>
      )}

      {timeline.length > 0 && (
        <div data-testid="analytics-timeline">
          <div className="overline mb-3">Actividad diaria</div>
          <div className="flex gap-1 items-end" style={{ height: "120px" }}>
            {timeline.map((d, i) => (
              <div key={i} className="flex-1 relative group" title={`${d.date}: ${d.count} eventos`}>
                <div
                  style={{
                    background: "var(--brand-primary)",
                    height: `${(d.count / max) * 100}%`,
                    minHeight: d.count > 0 ? "4px" : "1px",
                    opacity: d.count > 0 ? 1 : 0.2,
                    transition: "opacity 0.2s",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
